// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * Shared drawing conventions for every one-line glyph.
 *
 * One grid and one stroke weight across the whole set, so the symbols read as
 * a family rather than as seven separate drawings. Each glyph paints in
 * `currentColor`, which lets the chip's CSS decide the colour — that is what
 * keeps status and theming out of the SVG and inside the theme (ADR-0007).
 */

/** Every glyph is drawn on the same 24×24 grid. */
export const GLYPH_VIEWBOX = '0 0 24 24';

/** One stroke weight across the set. */
export const GLYPH_STROKE = 1.6;

export interface SymbolGlyphProps {
  /** Rendered edge length in px. The chip draws at 18. */
  size?: number;
  /**
   * Run state, honoured by rotating machines only: filled when running, an
   * outline when stopped, a dashed outline when nothing says. The fill is CSS
   * (`.sld-glyph--running`), so the SVG stays in `currentColor`.
   */
  run?: RunState;
  /**
   * Which conductor leads to draw. Both by default; the stage drops the top
   * lead of a device nothing feeds and the bottom lead of one that feeds
   * nothing, so a chain ends at its icon rather than a stub pointing onward.
   */
  leads?: GlyphLeads;
}

export interface GlyphLeads {
  top?: boolean;
  bottom?: boolean;
}

export type RunState = 'running' | 'stopped' | 'unknown';

/** Class list for a glyph's root, naming its run state when it has one. */
export const glyphClass = (run?: RunState): string =>
  run ? `sld-glyph sld-glyph--${run}` : 'sld-glyph';
